import React, { useCallback } from 'react';
import { useSelector, withFullHydrate } from '@ecommerce/nest-ssr/common';
import reducers from '../../reducers';
import MainLayoutDesktop from 'layouts/main-layout/main-layout.desktop';
import BreadcrumbsDesktop from 'modules/shared/components/breadcrumbs/breadcrumbs.desktop';
import { ProducerPageProps, ProducerProductsInterface } from 'modules/producers/views/pages/producer-page.props';
import Container from 'atomic/container';
import ProductViewType from 'modules/categories/views/components/product-view-type';
import { ListingType, ListingTypesEnum } from 'modules/shared/modules/categories-shared/interfaces/category-listing-config.interface';
import { ProductBoxWideProps } from 'modules/shared/components/product/product-box-wide/product-box-wide.props';
import { ProductBoxNarrowProps } from 'modules/shared/components/product/product-box-narrow/product-box-narrow.props';
import ProductBoxWideDesktop from 'modules/shared/components/product/product-box-wide/product-box-wide-desktop';
import ProductBoxNarrow from 'modules/shared/components/product/product-box-narrow';
import ProductBoxLifestyleDesktop from 'modules/shared/components/product/product-box-lifestyle/product-box-lifestyle-desktop';
import SearchNoResult from 'modules/search/views/components/search-no-result';
import PageDetails from 'modules/categories/views/components/page-details';
import { useSeo } from 'hooks/use-seo';
import { useProducerUrl } from 'modules/producers/hooks/use-producer-url';
import PaginationMini from 'components/paginations/pagination-mini';
import Pagination from 'components/paginations/pagination';
import FilterActiveDesktop from 'modules/shared/components/filters/components/filters-active/filters-active-desktop';
import { FiltersActions } from '../../reducers/producer-url-params.reducer';
import FiltersDesktop from 'modules/shared/components/filters/components/filters-desktop';
import { useProducerFilters } from 'modules/producers/hooks/use-producer-filters';
import { ProducerStateInterface } from 'modules/producers/interfaces/producer-state.interface';
import AdSlotBelowFilters from 'modules/categories/ads/ad-slot-below-filters';
import { SLOT_CATEGORY_BELOW_FILTERS_RIGHT } from 'components/ads/ad-slot/ad-defs';
import { prepareProducerCategoryTree } from '../../helpers/prepare-producer-category-tree.helpers';
import ProducerCategoryTreeDesktop from 'modules/producers/views/components/producer-category-tree/producer-category-tree-desktop';
import { getListingSection } from 'helpers/listing-section';
import ProducerHead from 'modules/producers/views/components/producer-head';
import Newsletter from 'components/newsletter';
import WidgetDSATitle from 'modules/shared/widgets/components/widget-dsa/components/widget-dsa-title';
import { showDSA } from 'modules/shared/widgets/components/widget-dsa/helpers/dsa-helpers';
import AdSlotPanel from 'components/ads/ad-slot-panel';
import AdSlotTop from 'components/ads/ad-slot-top';
import Dropdown from 'atomic/dropdown/dropdown';
import { makeSrcSet } from 'modules/shared/helpers/image-sources';

import './producer.desktop.scss';

const ProducerDesktop: React.FC<ProducerPageProps<ProductBoxWideProps | ProductBoxNarrowProps>> = ({
    breadcrumbs,
    producer,
    category,
    pageTypeName,
    pageLimitItem,
    showDiscounts,
    producerCategoryTree
}) => {
    const CATEGORY_TREE_LIMIT = 3;
    const getUrl = useProducerUrl();
    const filters = useProducerFilters();
    const products = useSelector((state: { products: ProducerProductsInterface<ProductBoxWideProps | ProductBoxNarrowProps>; }) => state.products);
    const listingType = useSelector((state: { listingType: ListingType; }) => state.listingType ?? ListingTypesEnum.List);
    const seo = useSeo();
    const getPaginationUrl = useCallback((page: number) => getUrl({ page, query: {} }), [getUrl]);
    const producerUrlManager = useSelector((state: ProducerStateInterface) => state.producerUrlManager);
    const categoriesTree = prepareProducerCategoryTree(getUrl, producerCategoryTree, producer.slug, producer.name, category ? category.id : null);

    const onSelect = (value: string): void => {
        window.location.replace(getUrl({ sort: value, page: 1, query: {} }));
    };

    const sortList = [
        { value: '', label: 'Popularność' },
        { value: 'price-asc', label: 'Cena rosnąco' },
        { value: 'price-desc', label: 'Cena malejąco' },
        { value: 'rating', label: 'Najlepsze opinie' }
    ];

    if (showDiscounts) {
        sortList.push({ value: 'discount', label: 'Obniżka' });
    }

    return (
        <MainLayoutDesktop>
            <ProducerHead robotsContent={seo.robotsContent} />
            <div className="producer-page">
                <Container className="screening-bg">
                    <div className="row">
                        <div className="col col-12">
                            <AdSlotTop />
                        </div>
                        <div className="col col-12">
                            <BreadcrumbsDesktop breadcrumbs={breadcrumbs} />
                        </div>
                        {producer.config?.dsa && showDSA(producer.config.dsa) && (
                            <div className="col col-12 producer-page__dsa">
                                <WidgetDSATitle dsa={producer.config.dsa} />
                            </div>
                        )}
                        <div className="col col-12">
                            <div className="producer-page__title">
                                <div className="producer-page__text">
                                    Strefa
                                </div>
                                {producer.mainLogoSources?.desktop && (
                                    <img className="producer-page__logo"
                                        src={producer.mainLogoSources.desktop.regular}
                                        srcSet={makeSrcSet(producer.mainLogoSources.desktop.regular, producer.mainLogoSources.desktop.retina)}
                                        alt={producer.name}
                                        aria-label={producer.name}
                                        width={90}
                                        height={45}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col col-3 pr-16" data-section="filters">
                            {Boolean(categoriesTree.length) && (
                                <div className="category-tree-d">
                                    <ProducerCategoryTreeDesktop
                                        categoriesTree={categoriesTree}
                                        category={category}
                                        producerName={producer.name}
                                        producerSlug={producer.slug}
                                        getUrl={getUrl}
                                        h1={seo.h1}
                                        categoryTreeLimit={CATEGORY_TREE_LIMIT}
                                    />
                                </div>
                            )}
                            {Boolean(filters.groups.length) && (
                                <FilterActiveDesktop
                                    getUrl={getUrl}
                                    filtersIds={producerUrlManager.filters}
                                    filters={filters}
                                />
                            )}
                            {Boolean(filters.groups.length) && (
                                <FiltersDesktop
                                    showPriceFilter={false}
                                    filters={filters}
                                    getUrl={getUrl}
                                    filtersActions={FiltersActions}
                                />
                            )}
                            <div className="producer-page__ad-section">
                                <AdSlotBelowFilters name={SLOT_CATEGORY_BELOW_FILTERS_RIGHT} />
                            </div>
                        </div>
                        <div className="col col-9 pl-16">
                            <div className="row producer-page__header">
                                <div className="col col-6 producer-page__header--left">
                                    <ProductViewType pageTypeName={pageTypeName} />
                                    <PageDetails
                                        page={producerUrlManager.page}
                                        count={products.count}
                                        listingCount={pageLimitItem}
                                    />
                                </div>
                                <div className="col col-6 producer-page__header--right">
                                    <Dropdown
                                        className="category-sort"
                                        list={sortList}
                                        defaultValue={producerUrlManager.sort}
                                        onChange={onSelect}
                                        label="Sortuj:"
                                        isTransparent={true}
                                    />
                                    <PaginationMini
                                        page={producerUrlManager.page}
                                        count={products.count}
                                        limit={pageLimitItem}
                                        getUrl={getPaginationUrl}
                                    />
                                </div>
                            </div>
                            <div className="producer-page-list row" data-section={getListingSection(listingType)}>
                                {products.count === 0 && (
                                    <div className="col col-12">
                                        <SearchNoResult
                                            query={producer.name}
                                            categoryName={category?.name}
                                        />
                                    </div>
                                )}
                                {listingType === ListingTypesEnum.List && (
                                    products.items.map((product) => (
                                        <div
                                            className="col col-12"
                                            key={product.id}
                                        >
                                            <ProductBoxWideDesktop product={product} />
                                        </div>
                                    )))
                                }
                                {listingType === ListingTypesEnum.Grid && (
                                    products.items.map((product) => (
                                        <div
                                            className="producer-page-list__grid-box col col-3"
                                            key={product.id}
                                        >
                                            <ProductBoxNarrow product={product} />
                                        </div>
                                    )))
                                }
                                {listingType === ListingTypesEnum.Lifestyle && (
                                    products.items.map((product) => (
                                        <div
                                            className="col col-4"
                                            key={product.id}
                                        >
                                            <ProductBoxLifestyleDesktop product={product} />
                                        </div>
                                    )))
                                }
                            </div>
                            <div className="row">
                                <div className="col col-12">
                                    <Pagination
                                        page={producerUrlManager.page}
                                        count={products.count}
                                        limit={pageLimitItem}
                                        getUrl={getPaginationUrl}
                                    />
                                </div>
                                <div className="col col-12">
                                    <AdSlotPanel />
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
            <Newsletter isMobile={false} />
        </MainLayoutDesktop>
    );
};

export default withFullHydrate(ProducerDesktop, { root: '#root', reducers }, 'modules/producers/views/pages/producer.desktop');
